import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePageTitle, useStatoInSolaLettura } from '../../../layouts/PageHeaderExtra';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import {
    CERTIFICATI_SIMULAZIONE,
    ESITI_RICHIESTA,
    STATI_CERTIFICATO,
    annullaRichiesta,
    richiestaEliminabile,
    elencoCertificati,
    simulaRispostaPlayer,
    sollecitaRichiesta,
    verificaCertificato,
} from '../../../../api-client/certificatiMedici';
import { FinestraRichiesta, dataIt } from './index';
import { apriFile } from '../../../components/Util/anteprimaFile';
import { chiediConferma } from '../../../components/Util/chiediConferma';

/*
 * Scheda del certificato medico di un tesserato: titolo = nome, stato del certificato accanto al titolo
 * (in sola lettura: lo decide la risposta del player e l'ultima richiesta). Sotto, l'elenco di tutte le
 * richieste a questo tesserato: cliccando una riga si apre il riquadro con i dettagli ed «Elimina richiesta»
 * (se non è approvata; altrimenti il perché). In fondo: Sollecita / Nuova richiesta.
 */

const oraIt = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

/**
 * Registro della richiesta: la richiesta inviata (prima riga), i solleciti automatici e manuali, la risposta
 * del player o l'annullamento. Ogni sollecito = email e notifica nell'app.
 */
const registro = (req, destinatario) => {
    if (!req) return [];
    const righe = [{ at: req.createdAt, evento: 'Richiesta inviata', dettaglio: req.message ? `Nota: ${req.message}` : '', a: destinatario }];
    (req.reminders || []).forEach((r) => righe.push({
        at: r.sentAt,
        evento: r.type === 'AUTO' ? `${r.number}° sollecito automatico` : 'Sollecito inviato dalla struttura',
        dettaglio: '',
        a: destinatario,
    }));
    // Anche una richiesta poi eliminata conserva la risposta del player (accettata se ha una verifica)
    const risposta = ['ACCEPTED', 'REFUSED'].includes(req.status) ? req.status : req.respondedAt ? (req.review ? 'ACCEPTED' : 'REFUSED') : '';
    if (risposta) {
        righe.push({ at: req.respondedAt, evento: risposta === 'ACCEPTED' ? 'Il player ha accettato' : 'Il player ha rifiutato', dettaglio: req.reason ? `Motivazione: ${req.reason}` : '', a: 'Email alla struttura' });
    }
    if (req.review && req.review.status !== 'PENDING' && req.review.at) {
        righe.push({
            at: req.review.at,
            evento: req.review.status === 'APPROVED' ? 'Certificato approvato dalla struttura' : 'Certificato non approvato dalla struttura',
            dettaglio: req.review.reason ? `Motivo: ${req.review.reason}` : '',
            a: destinatario,
        });
    }
    if (req.status === 'CANCELLED') righe.push({ at: req.cancelledAt, evento: 'Richiesta eliminata dalla struttura', dettaglio: '', a: '' });
    return righe.sort((x, y) => String(x.at || '').localeCompare(String(y.at || '')));
};

const VERIFICHE = { PENDING: 'Da verificare', APPROVED: 'Approvato', REJECTED: 'Non approvato' };
const verificaDi = (r) => (r.status === 'ACCEPTED' ? VERIFICHE[r.review?.status || 'PENDING'] : '—');

const Valore = ({ etichetta, children }) => (
    <Col md={6}>
        <Form.Label>{etichetta}</Form.Label>
        <div className="sid-readonly-value">{children || '—'}</div>
    </Col>
);

const COLONNE_RICHIESTE = [
    { Header: 'Inviata il', accessor: 'inviata', sortType: (a, b) => String(a.original.createdAt).localeCompare(String(b.original.createdAt)) },
    { Header: 'Esito', accessor: 'esito' },
    { Header: 'Verifica', accessor: 'verifica' },
];

/** Il registro di tutte le richieste, in ordine di tempo; con più richieste ogni evento dice a quale si riferisce. */
const registroCompleto = (richieste, destinatario) => richieste
    .flatMap((req) => registro(req, destinatario).map((r) => (richieste.length > 1
        ? { ...r, dettaglio: [r.dettaglio, `Richiesta del ${dataIt(req.createdAt)}`].filter(Boolean).join(' · ') }
        : r)))
    .sort((x, y) => String(x.at || '').localeCompare(String(y.at || '')));

export const SchedaCertificato = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const tab = new URLSearchParams(location.search).get('tab') === 'log' ? 'log' : 'certificato';
    const scegliTab = (k) => navigate(k === 'log' ? `${location.pathname}?tab=log` : location.pathname, { replace: true });
    const [elenco, setElenco] = useState(null);
    const [errore, setErrore] = useState('');
    const [avviso, setAvviso] = useState('');
    const [aperta, setAperta] = useState(null);
    const [nuova, setNuova] = useState(false);
    const [motivoSimulato, setMotivoSimulato] = useState('');
    const [motivoVerifica, setMotivoVerifica] = useState('');
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        const r = await elencoCertificati();
        if (!r || r.error) { setErrore(r?.error || 'Impossibile leggere il certificato.'); setElenco([]); return; }
        setElenco(r);
    }, []);
    useEffect(() => { carica(); }, [carica]);

    const c = elenco ? elenco.find((x) => x.personId === id) : null;
    const req = c?.request || null;
    const richieste = c?.requests || (req ? [req] : []);
    const inAttesa = req?.status === 'PENDING';
    const stato = c ? STATI_CERTIFICATO[c.certificateStatus] : null;

    usePageTitle(c?.name);
    useStatoInSolaLettura(stato?.label, stato?.tono === 'scaduto' ? 'non-attivo' : stato?.tono);

    useEffect(() => {
        if (!avviso) return undefined;
        const t = setTimeout(() => setAvviso(''), 3000);
        return () => clearTimeout(t);
    }, [avviso]);

    const apri = (r) => { setAperta(aperta === r.id ? null : r.id); };
    const elimina = async (r) => {
        if (!(await chiediConferma({
            titolo: 'Eliminare la richiesta?',
            testo: `Nel web player sparisce, senza avvisi${r.status === 'ACCEPTED' ? ', e non vedrai più il certificato mandato' : ''}.`,
            conferma: 'Elimina',
        }))) return;
        setInCorso(true);
        const esito = await annullaRichiesta(r.id);
        setInCorso(false);
        if (esito?.error) { setErrore(esito.error); return; }
        setAperta(null);
        setAvviso('Richiesta eliminata.');
        carica();
    };
    const sollecita = async () => {
        setInCorso(true);
        const r = await sollecitaRichiesta(req.id);
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setAvviso(`Sollecito inviato a ${c.name}: email e notifica nell'app.`);
        carica();
    };
    const verifica = async (approva) => {
        setInCorso(true);
        const r = await verificaCertificato(req.id, approva, motivoVerifica);
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setMotivoVerifica('');
        setAvviso(approva ? 'Certificato approvato.' : 'Certificato non approvato: il player riceve un\'email e una notifica.');
        carica();
    };
    const rispondiComePlayer = async (accetta) => {
        setInCorso(true);
        const r = await simulaRispostaPlayer(req.id, accetta, motivoSimulato);
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setMotivoSimulato('');
        setAvviso(accetta ? 'Il player ha accettato: ti arriva un\'email.' : 'Il player ha rifiutato: ti arriva un\'email con la motivazione.');
        carica();
    };

    // Riquadro sotto la riga della richiesta: i dettagli e, se non è approvata, «Elimina richiesta»
    const pannelloRichiesta = (r) => {
        const approvata = r.status === 'ACCEPTED' && r.review?.status === 'APPROVED';
        return (
            <div className="sid-occ-panel">
                <Row className="g-3">
                    {r.message && <Valore etichetta="Nota della struttura">{r.message}</Valore>}
                    {r.reason && <Valore etichetta="Motivazione del player">{r.reason}</Valore>}
                    {r.review?.reason && <Valore etichetta="Motivo del «Non approvato»">{r.review.reason}</Valore>}
                    {!r.message && !r.reason && !r.review?.reason && <Col md={12}><span className="sid-nota-leggibile">Nessuna nota o motivazione.</span></Col>}
                </Row>
                <div className="d-flex justify-content-between align-items-center gap-2 mt-4">
                    <div className="d-flex align-items-center gap-2">
                        {richiestaEliminabile(r) && (
                            <Button size="sm" className="btn btn-danger light sid-btn-compact" disabled={inCorso} onClick={() => elimina(r)}>Elimina richiesta</Button>
                        )}
                        {approvata && <small className="sid-nota-leggibile">Certificato approvato,<br />richiesta non eliminabile.</small>}
                        {r.status === 'CANCELLED' && <small className="sid-nota-leggibile">Richiesta eliminata.</small>}
                    </div>
                    <Button size="sm" className="btn btn-light sid-btn-compact" onClick={() => setAperta(null)}>Chiudi</Button>
                </div>
            </div>
        );
    };

    if (!elenco) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;
    if (!c) {
        return (
            <Card><Card.Body className="sid-card-body--spaced">Tesserato non trovato.</Card.Body></Card>
        );
    }

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>
            <FinestraRichiesta
                show={nuova}
                onHide={() => setNuova(false)}
                tesserati={elenco}
                personId={c.personId}
                modoIniziale={req ? 'richiedi' : 'carica'}
                onInviata={(t, modo) => { setNuova(false); setAvviso(modo === 'carica' ? 'Certificato caricato: vale subito.' : 'Richiesta inviata: il player riceve un\'email e una notifica nell\'app.'); carica(); }}
            />

            <div className="custom-tab-1">
            <Tab.Container activeKey={tab} onSelect={scegliTab}>
            <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                <Nav.Item as="li" className="tabMenu"><Nav.Link eventKey="certificato">Certificato</Nav.Link></Nav.Item>
                <Nav.Item as="li" className="tabMenu"><Nav.Link eventKey="log">Dettaglio richieste</Nav.Link></Nav.Item>
            </Nav>
            <Tab.Content className="mt-4">
            <Tab.Pane eventKey="certificato">
            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <h5 className="sid-form-section">Certificato</h5>
                    <Row className="g-4">
                        <Valore etichetta="Tesserato">{`${c.name} · ${c.personType === 'JUNIOR' ? 'Junior' : 'Player'}`}</Valore>
                        <Valore etichetta="Scadenza">{dataIt(c.expiration)}</Valore>
                        <Valore etichetta="Documento">
                            {c.documentUrl
                                ? <a href={c.documentUrl} onClick={(e) => { e.preventDefault(); apriFile({ uri: c.documentUrl, nome: c.documentName || '' }); }}>{c.documentName || 'Apri il certificato'}</a>
                                : (c.documentName || '')}
                            {(c.documentUrl || c.documentName) && (
                                <small className="d-block text-muted">{c.uploadedByClub ? `Caricato dalla struttura il ${dataIt(c.uploadedAt)}` : 'Mandato dal player'}</small>
                            )}
                        </Valore>
                    </Row>
                    {c.certificateStatus === 'TO_VERIFY' && (
                        <div className="sid-verifica mt-4">
                            <div className="sid-verifica__titolo">Verifica il certificato</div>
                            <p className="mb-3">
                                {c.name} ha mandato il certificato: aprilo, controlla che sia quello giusto e che la scadenza sia corretta,
                                poi approvalo. Se non va bene, scrivi il motivo: {c.personType === 'JUNIOR' ? 'il genitore' : 'il player'} riceve
                                un'email e una notifica e ne carica un altro.
                            </p>
                            <Form.Label htmlFor="cert-motivo-verifica">Motivo (serve solo per «Non approvare»)</Form.Label>
                            <Form.Control id="cert-motivo-verifica" as="textarea" rows={2} maxLength={500} value={motivoVerifica} onChange={(e) => setMotivoVerifica(e.target.value)} />
                            <div className="d-flex gap-2 mt-3">
                                <Button className="btn btn-primary btn-add" disabled={inCorso} onClick={() => verifica(true)}>Approva</Button>
                                <Button className="btn btn-dark light btn-add" disabled={inCorso} onClick={() => verifica(false)}>Non approvare</Button>
                            </div>
                        </div>
                    )}
                    {c.certificateStatus === 'NOT_APPROVED' && req?.review?.reason && (
                        <p className="sid-nota-leggibile mt-4 mb-0">Non approvato il {dataIt(req.review.at)}: {req.review.reason}. In attesa che il player carichi un altro certificato.</p>
                    )}
                    {c.certificateStatus === 'REQUESTED' && req?.status === 'ACCEPTED' && (
                        <p className="sid-nota-leggibile mt-4 mb-0">Il player ha accettato ma non ha ancora caricato il certificato.</p>
                    )}

                    {!req && <p className="sid-nota-leggibile mt-4 mb-0">Non hai ancora chiesto il certificato a questo tesserato.</p>}

                    {CERTIFICATI_SIMULAZIONE && inAttesa && (
                        <div className="sid-simula-risposta mt-4">
                            <div className="sid-simula-risposta__titolo">Simulazione: rispondi come il player</div>
                            <p className="mb-2">Nel sistema vero il player risponde dal web player e a te arriva un'email.</p>
                            <Form.Label htmlFor="cert-motivo">Motivazione (facoltativa)</Form.Label>
                            <Form.Control id="cert-motivo" as="textarea" rows={2} maxLength={500} value={motivoSimulato} onChange={(e) => setMotivoSimulato(e.target.value)} />
                            <div className="d-flex gap-2 mt-3">
                                <Button className="btn btn-primary btn-add" disabled={inCorso} onClick={() => rispondiComePlayer(true)}>Accetta</Button>
                                <Button className="btn btn-dark light btn-add" disabled={inCorso} onClick={() => rispondiComePlayer(false)}>Rifiuta</Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
            {richieste.length > 0 && (
                <div className="mt-4">
                    <TabellaLista
                        cardTitle={richieste.length > 1 ? `Richieste (${richieste.length})` : 'Richieste'}
                        pageList={false}
                        hasSearch={false}
                        hasFilter={false}
                        openColumn="inviata"
                        openRow={apri}
                        rowPanel={(r) => (r.id === aperta ? pannelloRichiesta(r) : null)}
                        data_column={COLONNE_RICHIESTE}
                        sort_by="inviata"
                        sort_type="desc"
                        data_json={richieste.map((r) => ({
                            ...r,
                            inviata: dataIt(r.createdAt),
                            esito: `${ESITI_RICHIESTA[r.status]}${r.respondedAt ? ` il ${dataIt(r.respondedAt)}` : r.cancelledAt ? ` il ${dataIt(r.cancelledAt)}` : ''}`,
                            verifica: verificaDi(r),
                        }))}
                    />
                </div>
            )}
            </Tab.Pane>
            <Tab.Pane eventKey="log">
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        {req ? (
                            <>
                                <p className="sid-nota-leggibile">
                                    Tutto quello che è successo alle richieste: invio, solleciti (ognuno manda al player un'email e una notifica
                                    nell'app; quelli automatici partono dopo i giorni impostati da SportID), risposta del player e verifica.
                                </p>
                                <div className="table-responsive">
                                    <table className="table sid-tabella-registro mb-0">
                                        <thead><tr><th>Data</th><th>Evento</th><th>Inviato a</th></tr></thead>
                                        <tbody>
                                            {registroCompleto(richieste, c.email).map((r, i) => (
                                                <tr key={i}>
                                                    <td className="text-nowrap">{oraIt(r.at)}</td>
                                                    <td>{r.evento}{r.dettaglio && <small className="d-block text-muted">{r.dettaglio}</small>}</td>
                                                    <td>{r.a}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <p className="sid-nota-leggibile mb-0">Nessuna richiesta inviata a questo tesserato.</p>
                        )}
                    </Card.Body>
                </Card>
            </Tab.Pane>
            </Tab.Content>
            </Tab.Container>
            </div>

            <div className="d-flex justify-content-end gap-4 mt-4">
                <div className="d-flex gap-4">
                    {inAttesa
                        ? <Button className="btn btn-primary" disabled={inCorso} onClick={sollecita}>Sollecita</Button>
                        : <Button className="btn btn-primary" disabled={inCorso} onClick={() => setNuova(true)}>Nuova richiesta</Button>}
                </div>
            </div>

            {avviso && <div className="sid-avviso-salvato" role="status">✓ {avviso}</div>}
        </>
    );
};

export default SchedaCertificato;
